import { SyntheticEvent, cloneElement } from "react";

import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Tabs from "@mui/material/Tabs";

import Tab from "../../../components/styled/Tab";
import { SettingsSubNavItem } from "./useSettingsSubNavState";

export type SettingsSubNavProps = {
  items: SettingsSubNavItem[];
  activeId: string;
  onSelect: (id: string) => void;
};

export type SettingsSubNavMobileDropdownProps = {
  items: SettingsSubNavItem[];
  activeId: string;
  onSelect: (id: string) => void;
};

export const SettingsSubNavMobileDropdown = ({
  items,
  activeId,
  onSelect,
}: SettingsSubNavMobileDropdownProps) => {
  const tabValue = items.some((item) => item.id === activeId)
    ? activeId
    : (items[0]?.id ?? "");

  return (
    <Box
      sx={{
        display: { xs: "block", md: "none" },
        position: "sticky",
        top: "var(--top-nav-height)",
        zIndex: 1,
        bgcolor: "background.default",
        pt: 1,
        pb: 1,
      }}
    >
      <Select
        value={tabValue}
        onChange={(e) => onSelect(String(e.target.value))}
        fullWidth
        sx={{ bgcolor: "background.paper" }}
        renderValue={(selected) => {
          const selectedItem = items.find((item) => item.id === selected);
          if (!selectedItem) return null;
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {selectedItem.icon}
              {selectedItem.label}
            </Box>
          );
        }}
        MenuProps={{
          disableScrollLock: true,
          PaperProps: { style: { maxHeight: 300 } },
        }}
      >
        {items.map((item) => (
          <MenuItem key={item.id} value={item.id}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {item.icon}
              {item.label}
            </Box>
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
};

const SettingsSubNav = ({ items, activeId, onSelect }: SettingsSubNavProps) => {
  // Ensure the controlled value always matches an existing tab.
  const tabValue = items.some((item) => item.id === activeId)
    ? activeId
    : (items[0]?.id ?? false);

  const handleTabChange = (_event: SyntheticEvent, newValue: string): void => {
    onSelect(newValue);
  };

  return (
    <Box
      sx={(theme) => ({
        display: { xs: "none", md: "block" },
        position: "sticky",
        top: "calc(var(--top-nav-height) + 16px)",
        maxHeight: "calc(100vh - var(--sticky-edit-bar-height) - 32px)",
        overflowY: "auto",
        scrollbarWidth: "thin",
        scrollbarColor: `${theme.palette.action.active} transparent`,
        "&::-webkit-scrollbar": { width: 8 },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.action.active,
          borderRadius: 4,
        },
        "&:hover::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.primary.main,
        },
        "&::-webkit-scrollbar-thumb:hover": {
          backgroundColor: theme.palette.primary.main,
        },
      })}
    >
      <Tabs
        aria-label="Settings sections"
        orientation="vertical"
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        selectionFollowsFocus
        value={tabValue}
        variant="fullWidth"
      >
        {items.map((item: SettingsSubNavItem) => (
          <Tab
            key={item.id}
            label={item.label}
            icon={cloneElement(item.icon, {
              color: "inherit",
              sx: { pr: 0.5 },
            })}
            iconPosition="start"
            value={item.id}
            id={`settings-subnav-tab-${item.id}`}
            aria-controls={item.id}
            disableFocusRipple
            disableRipple
          />
        ))}
      </Tabs>
    </Box>
  );
};

export default SettingsSubNav;
